import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Paper,
    Tab,
    Tabs,
    Button,
    Fade,
    Alert
} from '@mui/material';
import {
    TrendingUp, School, History, EmojiEvents, Star,
    CardGiftcard
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getRewardHistory, getBadges, getUserStats } from '../api/rewards';

// Import modular components
import RewardsHeader from '../components/Rewards/RewardsHeader';
import UserStatsOverview from '../components/Rewards/UserStatsOverview';
import BadgesShowcase from '../components/Rewards/BadgesShowcase';
import RewardHistoryWidget from '../components/Rewards/RewardHistoryWidget';
import ProgressTracker from '../components/Rewards/ProgressTracker';
import LevelProgressCard from '../components/Rewards/LevelProgressCard';

// Updated ECO_LEVELS system matching your documentation
const ECO_LEVELS = [
    { name: "Rookie", minPoints: 0, maxPoints: 499, color: "#66bb6a", icon: "🌱" },
    { name: "Apprentice", minPoints: 500, maxPoints: 1999, color: "#43a047", icon: "⚔️" },
    { name: "Sorter", minPoints: 2000, maxPoints: 4999, color: "#2e7d32", icon: "♻️" },
    { name: "Pro Sorter", minPoints: 5000, maxPoints: 14999, color: "#1b5e20", icon: "🌍" },
    { name: "Master Sorter", minPoints: 15000, maxPoints: 49999, color: "#0d4717", icon: "👑" },
    { name: "Eco-Legend", minPoints: 50000, maxPoints: Infinity, color: "#004d40", icon: "🏆" }
];

const RewardsPage = () => {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [userStats, setUserStats] = useState(null);
    const [userBadges, setUserBadges] = useState([]);
    const [rewardHistory, setRewardHistory] = useState([]);
    const [error, setError] = useState(null);

    // Redirect non-authenticated users
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (isAuthenticated && user) {
            fetchUserData();
        }
    }, [isAuthenticated, user]);

    const getEcoLevel = (points) => {
        return ECO_LEVELS.find(level =>
            points >= level.minPoints && points <= level.maxPoints
        ) || ECO_LEVELS[0];
    };

    const getNextLevel = (points) => {
        const currentLevel = getEcoLevel(points);
        const currentIndex = ECO_LEVELS.findIndex(level => level.name === currentLevel.name);
        return currentIndex < ECO_LEVELS.length - 1 ? ECO_LEVELS[currentIndex + 1] : null;
    };

    const calculateLevelProgress = (points) => {
        const currentLevel = getEcoLevel(points);
        const nextLevel = getNextLevel(points);

        if (!nextLevel) return 100;

        const pointsInCurrentLevel = points - currentLevel.minPoints;
        const pointsNeededForNextLevel = nextLevel.minPoints - currentLevel.minPoints;

        return Math.min(100, (pointsInCurrentLevel / pointsNeededForNextLevel) * 100);
    };

    const fetchUserData = async () => {
        setLoading(true);
        setError(null);

        try {
            const [historyRes, badgesRes, statsRes] = await Promise.all([
                getRewardHistory().catch(() => ({ data: [] })),
                getBadges().catch(() => ({ data: [] })),
                getUserStats().catch(() => ({ data: null }))
            ]);

            const rewards = historyRes.data || [];
            const badges = badgesRes.data || [];
            const stats = statsRes.data || {};

            setRewardHistory(rewards);
            setUserBadges(badges);

            // Create comprehensive user stats
            const totalPoints = user?.rewardsPoints || 0;
            const currentLevel = getEcoLevel(totalPoints);
            const nextLevel = getNextLevel(totalPoints);
            const progressPercentage = calculateLevelProgress(totalPoints);

            setUserStats({
                totalPoints,
                currentLevel,
                nextLevel,
                progressPercentage,
                totalBadges: badges.length,
                totalClassifications: stats.totalClassifications || 0,
                currentStreak: stats.currentStreak || 0,
                accuracyRate: stats.averageAccuracy || 0,
                weeklyPoints: stats.weeklyPoints || 0,
                monthlyPoints: stats.monthlyPoints || 0,
                globalRank: stats.globalRank || 0,
                lastActivity: stats.lastActivity || new Date()
            });

        } catch (error) {
            console.error('Error fetching user rewards data:', error);
            setError('Failed to load rewards data. Please refresh the page.');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const tabConfig = [
        {
            id: 'overview',
            label: 'Overview',
            icon: <Star />,
            description: 'Your eco journey summary'
        },
        {
            id: 'badges',
            label: 'Badges',
            icon: <EmojiEvents />,
            description: 'Your earned achievements'
        },
        {
            id: 'history',
            label: 'History',
            icon: <History />,
            description: 'Your reward timeline'
        }
    ];

    // Show loading state
    if (loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f8faf8 0%, #e6f4ea 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Paper elevation={6} sx={{ p: 6, borderRadius: 4, textAlign: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                        <Box
                            component="div"
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 20px',
                                animation: 'pulse 2s infinite'
                            }}
                        >
                            <EmojiEvents sx={{ fontSize: 40, color: 'white' }} />
                        </Box>
                    </Box>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 2, color: '#43a047' }}>
                        Loading Your Eco Achievements...
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Gathering your environmental impact data
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // Show error state
    if (error) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Alert
                    severity="error"
                    sx={{ mb: 4, borderRadius: 3 }}
                    action={
                        <Button color="inherit" size="small" onClick={fetchUserData}>
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            background: `
                radial-gradient(circle at 20% 80%, rgba(67,160,71,0.12) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255,196,0,0.08) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(0,184,148,0.08) 0%, transparent 50%),
                linear-gradient(135deg, #f8fdf8 0%, #e8f5e8 50%, #f0f8f0 100%)
            `,
            position: 'relative',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `
                    url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2343a047' fill-opacity='0.02'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
                `,
                opacity: 0.3,
                animation: 'float 25s ease-in-out infinite',
                pointerEvents: 'none',
            },
            '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-15px)' },
            },
            '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
                '100%': { transform: 'scale(1)' },
            }
        }}>
            <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
                {/* Enhanced Header */}
                <RewardsHeader user={user} userStats={userStats} />

                {/* User Stats Overview Cards */}
                <UserStatsOverview userStats={userStats} user={user} />

                {/* Level Progress Card */}
                <LevelProgressCard userStats={userStats} />

                {/* Main Content Tabs */}
                <Paper elevation={8} sx={{
                    borderRadius: 4,
                    overflow: 'hidden',
                    mt: 4,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(20px)'
                }}>
                    <Box sx={{
                        background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                        color: 'white',
                        p: 3
                    }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            indicatorColor="secondary"
                            textColor="inherit"
                            variant="fullWidth"
                            sx={{
                                '& .MuiTab-root': {
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    minHeight: 64,
                                    color: 'rgba(255,255,255,0.8)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        color: 'white',
                                        background: 'rgba(255,255,255,0.1)',
                                        borderRadius: 3
                                    }
                                },
                                '& .Mui-selected': {
                                    color: 'white !important',
                                    background: 'rgba(255,255,255,0.2)',
                                    borderRadius: 3
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#ffc400',
                                    height: 4,
                                    borderRadius: 2
                                }
                            }}
                        >
                            {tabConfig.map((tab, index) => (
                                <Tab
                                    key={tab.id}
                                    icon={tab.icon}
                                    label={
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="body1" fontWeight={700}>
                                                {tab.label}
                                            </Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                {tab.description}
                                            </Typography>
                                        </Box>
                                    }
                                    iconPosition="start"
                                />
                            ))}
                        </Tabs>
                    </Box>

                    <Box sx={{ p: 4, minHeight: 600 }}>
                        <Fade in key={activeTab} timeout={500}>
                            <Box>
                                {activeTab === 0 && (
                                    <ProgressTracker
                                        userStats={userStats}
                                        userBadges={userBadges}
                                        rewardHistory={rewardHistory}
                                    />
                                )}
                                {activeTab === 1 && (
                                    <BadgesShowcase
                                        badges={userBadges}
                                        userStats={userStats}
                                        loading={loading}
                                    />
                                )}
                                {activeTab === 2 && (
                                    <RewardHistoryWidget
                                        history={rewardHistory}
                                        loading={loading}
                                    />
                                )}
                            </Box>
                        </Fade>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default RewardsPage;
